import React from "react";
import PageWrapper from "components/PageWrapper";
import { Box, Stack, Typography, Pagination } from "@mui/material";
import Header from "components/Header";
import useBoolean from "../../hooks/useBoolean";
import Filter, { FilterValue } from "./Filter";
import { persistParams, restorePayloadFromParams } from "../../utils/params";
import { useSearchParams } from "react-router-dom";
import { TPagination } from "../../model";
import { useNavigate } from "react-router";
import { UpliftAPI } from "../../api";
import { GetUpliftTrackerListParams } from "../../api/uplift";
import { UpliftTrackerOverview } from "../../api/transformers/uplift";
import { useSnackbar } from "notistack";
import ReviewCard from "./ReviewCard";

const PAGE_SIZE = 10;

function UpliftTrackerList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, { setTrue: startLoading, setFalse: stopLoading }] =
    useBoolean(false);
  const [initialFilter, initialPagination] = restorePayloadFromParams(
    searchParams,
    [paramsToFilter, paramsToPagination] as const
  );
  const [filter, setFilter] = React.useState<FilterValue>(initialFilter);
  const [pagination, setPagination] = React.useState<
    TPagination & { total: number }
  >({
    total: 0,
    ...initialPagination,
  });
  const [dataSource, setDataSource] = React.useState<UpliftTrackerOverview[]>(
    []
  );
  const { enqueueSnackbar } = useSnackbar();

  React.useEffect(() => {
    fetchData(filter, pagination);
  }, []);

  const fetchData = (f: FilterValue, p: TPagination) => {
    startLoading();
    persistParams(navigate, {
      ...f,
      page: p.page,
      pageSize: p.pageSize,
    });
    return UpliftAPI.getUpliftTrackerList(getPayload(f, p))
      .then((response) => {
        setDataSource(response.rows);
        setFilter(f);
        setPagination({
          total: response.total,
          page: p.page,
          pageSize: p.pageSize,
        });
      })
      .catch(() => {
        enqueueSnackbar("Failed to get uplift tracker List.", {
          variant: "error",
        });
      })
      .finally(() => {
        stopLoading();
      });
  };

  const handleFilterChange = (f: FilterValue, triggerFetch = false) => {
    if (triggerFetch) {
      fetchData(f, { page: 1, pageSize: pagination.pageSize });
    } else {
      setFilter(f);
    }
  };

  const handleSearch = () => {
    fetchData(filter, pagination);
  };

  const handleReset = () => {
    fetchData({}, { page: 1, pageSize: pagination.pageSize });
  };

  const handlePaginationChange = (
    e: React.ChangeEvent<unknown>,
    value: number
  ) => {
    fetchData(filter, { page: value, pageSize: pagination.pageSize });
  };

  return (
    <PageWrapper>
      <Box>
        <Header title="Uplift Status" subtitle="Review Uplift Tracker List" />
      </Box>
      <Filter
        value={filter}
        loading={isLoading}
        onChange={handleFilterChange}
        onSearch={handleSearch}
        onReset={handleReset}
      />
      <ReviewCard loading={isLoading} dataSource={dataSource} />
      <Stack
        justifyContent="space-between"
        direction="row"
        sx={{ marginTop: "14px" }}
      >
        <Typography>
          Showing {(pagination.page - 1) * PAGE_SIZE + 1}-
          {pagination.page * PAGE_SIZE} of {pagination.total}
        </Typography>
        <Pagination
          disabled={isLoading}
          count={Math.max(Math.ceil(pagination.total / PAGE_SIZE), 1)}
          page={pagination.page}
          onChange={handlePaginationChange}
        />
      </Stack>
    </PageWrapper>
  );
}

function getPayload(
  filter: FilterValue,
  pagination: TPagination
): GetUpliftTrackerListParams {
  return {
    ...filter,
    limit: String(pagination.pageSize),
    skip: String((pagination.page - 1) * pagination.pageSize),
  };
}

function paramsToFilter(sp: URLSearchParams): FilterValue {
  return {
    adviser: sp.get("adviser") || "",
    includeNoActionRequired: sp.get("includeNoActionRequired") || false,
    name: sp.get("name") || "",
    personId: sp.get("personId") || "",
    upliftDateFrom: sp.get("upliftDateFrom") || "",
    upliftDateTo: sp.get("upliftDateTo") || "",
    consideration: sp.get("consideration") || "",
    restrictions: sp.get("restrictions") || "",
  };
}

function paramsToPagination(sp: URLSearchParams): TPagination {
  return {
    page: sp.get("page") ? Number(sp.get("page")) : 1,
    pageSize: sp.get("pageSize") ? Number(sp.get("pageSize")) : PAGE_SIZE,
  };
}

export default UpliftTrackerList;
